import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI, contactsAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';
import { ArrowDownTrayIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const fmt = n => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2 });

const TYPE_LABEL = {
  sale:'Sales Invoice', purchase:'Purchase', expense:'Expense',
  indirect_income:'Income', quotation:'Quotation', proforma:'Proforma',
  delivery_challan:'Delivery Challan', debit_note:'Debit Note',
};
const TYPE_COLOR = {
  sale:'text-green-600 bg-green-50', purchase:'text-red-600 bg-red-50',
  expense:'text-orange-600 bg-orange-50', indirect_income:'text-blue-600 bg-blue-50',
  proforma:'text-violet-600 bg-violet-50', delivery_challan:'text-cyan-600 bg-cyan-50',
  debit_note:'text-pink-600 bg-pink-50',
};

export function LedgerReport() {
  const thisYear = new Date().getFullYear();
  const [search,    setSearch]   = useState('');
  const [contacts,  setContacts] = useState([]);
  const [selected,  setSelected] = useState(null);
  const [start,     setStart]    = useState(`${thisYear}-04-01`);
  const [end,       setEnd]      = useState(`${thisYear}-03-31`);
  const [ledger,    setLedger]   = useState(null);
  const [loading,   setLoading]  = useState(false);
  const [loadingC,  setLoadingC] = useState(false);
  const [filter,    setFilter]   = useState('all');

  // Load contacts for selector
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoadingC(true);
      try {
        const res = await contactsAPI.getAll({ search: search||undefined, limit:20 });
        setContacts(res.data||[]);
      } catch {}
      finally { setLoadingC(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadLedger = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await accountingAPI.getLedgerFull(selected._id, { startDate: start, endDate: end });
      setLedger(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [selected, start, end]);

  useEffect(() => { if (selected) loadLedger(); }, [loadLedger]);

  const records = (ledger?.records||[]).filter(r => filter==='all' || r.type===filter);

  return (
    <div className="space-y-5">
      <PageHeader title="Contact Ledger" subtitle="View complete transaction history for any customer or vendor" />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: Contact selector */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h3 className="font-semibold text-gray-800 text-sm">Select Contact</h3>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search customers / vendors..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {loadingC && <p className="text-xs text-gray-400 text-center py-4">Searching...</p>}
            {!loadingC && contacts.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No contacts found</p>}
            {contacts.map(c => (
              <button key={c._id} onClick={() => setSelected(c)}
                className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors border ${selected?._id===c._id ? 'border-indigo-300 bg-indigo-50' : 'border-transparent hover:bg-gray-50'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-3.5 h-3.5 text-indigo-600"/>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800 leading-tight">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.company||c.type}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Ledger */}
        <div className="lg:col-span-2 space-y-4">
          {/* Date filter */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex gap-3 flex-wrap items-end">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">From</label>
                <input type="date" value={start} onChange={e=>setStart(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">To</label>
                <input type="date" value={end} onChange={e=>setEnd(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
              </div>
              {selected && (
                <>
                  <button onClick={loadLedger} className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700">
                    Refresh
                  </button>
                  <button onClick={()=>window.print()} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                    <ArrowDownTrayIcon className="w-4 h-4"/> Print
                  </button>
                </>
              )}
            </div>
          </div>

          {!selected && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
              <UserIcon className="w-12 h-12 mx-auto text-gray-200 mb-3"/>
              <p className="text-gray-400 text-sm">Select a contact to view their ledger</p>
            </div>
          )}

          {selected && loading && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/>
            </div>
          )}

          {selected && !loading && ledger && (
            <>
              {/* Contact header */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{selected.name}</h3>
                    {selected.company && <p className="text-sm text-gray-500">{selected.company}</p>}
                    {selected.gstin   && <p className="text-xs font-mono text-gray-400 mt-0.5">GSTIN: {selected.gstin}</p>}
                    {selected.phone   && <p className="text-xs text-gray-400">📞 {selected.phone}</p>}
                  </div>
                  <div className="text-right space-y-1">
                    <div className="text-xs text-gray-400 uppercase font-semibold">Closing Balance</div>
                    <div className={`text-2xl font-bold ${ledger.closingBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(Math.abs(ledger.closingBalance))}
                    </div>
                    <div className="text-xs text-gray-400">{ledger.closingBalance >= 0 ? 'Net receivable' : 'Net payable'}</div>
                  </div>
                </div>
                {/* Summary row */}
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase font-semibold">Total Debit</p>
                    <p className="text-sm font-bold text-red-600">{fmt(ledger.totalDebit)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase font-semibold">Total Credit</p>
                    <p className="text-sm font-bold text-green-600">{fmt(ledger.totalCredit)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase font-semibold">Transactions</p>
                    <p className="text-sm font-bold text-gray-800">{ledger.records.length}</p>
                  </div>
                </div>
              </div>

              {/* Type filter tabs */}
              <div className="flex gap-2 flex-wrap">
                {['all','sale','purchase','expense'].map(f => (
                  <button key={f} onClick={()=>setFilter(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter===f?'bg-indigo-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    {f==='all'?'All':TYPE_LABEL[f]||f}
                  </button>
                ))}
              </div>

              {/* Ledger table */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Date','Ref No.','Type','Debit (₹)','Credit (₹)','Balance (₹)','Status'].map(h=>(
                          <th key={h} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left last:text-right">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {records.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No transactions found</td></tr>
                      )}
                      {records.map((r,i) => {
                        const isCredit = ['sale','indirect_income','proforma'].includes(r.type);
                        return (
                          <tr key={i} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{format(new Date(r.date),'dd MMM yyyy')}</td>
                            <td className="px-4 py-2.5 font-mono text-xs font-semibold text-indigo-600">{r.referenceNo}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-xs px-2 py-1 rounded-lg font-medium ${TYPE_COLOR[r.type]||'text-gray-600 bg-gray-100'}`}>
                                {TYPE_LABEL[r.type]||r.type}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-red-600 font-medium">{!isCredit ? fmt(r.total) : '—'}</td>
                            <td className="px-4 py-2.5 text-green-600 font-medium">{isCredit ? fmt(r.total) : '—'}</td>
                            <td className={`px-4 py-2.5 font-bold ${r.runningBalance>=0?'text-green-700':'text-red-700'}`}>{fmt(r.runningBalance)}</td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`text-xs px-2 py-1 rounded-lg font-medium capitalize
                                ${r.status==='paid'?'bg-green-50 text-green-700':r.status==='pending'?'bg-amber-50 text-amber-700':'bg-gray-50 text-gray-600'}`}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {records.length > 0 && (
                      <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-200">
                        <tr>
                          <td colSpan={3} className="px-4 py-2.5 text-gray-700">TOTAL ({records.length} transactions)</td>
                          <td className="px-4 py-2.5 text-red-600">{fmt(records.filter(r=>!['sale','indirect_income','proforma'].includes(r.type)).reduce((s,r)=>s+r.total,0))}</td>
                          <td className="px-4 py-2.5 text-green-600">{fmt(records.filter(r=>['sale','indirect_income','proforma'].includes(r.type)).reduce((s,r)=>s+r.total,0))}</td>
                          <td className={`px-4 py-2.5 text-lg ${ledger.closingBalance>=0?'text-green-700':'text-red-700'}`}>{fmt(ledger.closingBalance)}</td>
                          <td/>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}